import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { StatusPill } from "@/components/shared/StatusPill";
import { AvatarStack } from "@/components/work/PeoplePicker";
import { VisibilityPill } from "@/components/work/ItemRow";
import { fmtDate, isOverdue, STATUS_LABEL, STATUSES, TYPE_TONE, patchItem } from "@/lib/work";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { apiError } from "@/lib/api";

function Card({ item, onChanged }) {
  const [, setSp] = useSearchParams();
  return (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)} onClick={() => setSp((p) => { p.set("item", item.id); return p; })} data-testid={`kanban-card-${item.id}`}
      className={cn("bg-white rounded-xl border p-3 cursor-pointer shadow-sm hover:shadow-md transition-shadow border-l-[3px]", item.visibility === "external" ? "border-l-teal-400" : "border-l-slate-200")} style={{ borderColor: "var(--line)", borderLeftColor: item.visibility === "external" ? "#2dd4bf" : undefined }}>
      <div className="flex items-center justify-between mb-1.5"><span className="text-[10px] font-mono text-slate-400">{item.display_id}</span>{item.board_id && <VisibilityPill item={item} onChanged={onChanged} compact />}</div>
      <p className="text-sm font-medium text-slate-800 leading-snug">{item.title}</p>
      <div className="flex items-center justify-between mt-2.5">
        <div className="flex items-center gap-1.5">{item.work_type !== "task" && <StatusPill value={item.work_type} tone={TYPE_TONE[item.work_type]} />}<StatusPill value={item.priority} />{item.is_blocked && <span className="chip border-red-200 text-red-600 bg-red-50">blocked</span>}</div>
        <div className="flex items-center gap-2">{item.due_date && <span className={cn("text-[11px] tabular", isOverdue(item) ? "text-red-600 font-semibold" : "text-slate-400")}>{fmtDate(item.due_date)}</span>}<AvatarStack ids={item.assignee_ids} max={2} /></div>
      </div>
    </div>);
}

// groupBy: "status" | "section"
export function Kanban({ items, onChanged, groupBy = "status", sections = [] }) {
  const [over, setOver] = useState(null);
  const cols = groupBy === "section" ? sections.map((s) => ({ key: s.id, label: s.name })) : STATUSES.map((s) => ({ key: s, label: STATUS_LABEL[s] }));
  const field = groupBy === "section" ? "section_id" : "status";
  const drop = async (e, key) => {
    e.preventDefault(); setOver(null);
    const id = e.dataTransfer.getData("text/plain"); const it = items.find((x) => x.id === id);
    if (!it || it[field] === key) return;
    try { await patchItem(id, { [field]: key }); onChanged?.(); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1" data-testid="kanban">
      {cols.map((c) => { const list = items.filter((i) => (i[field] || (groupBy === "section" ? sections[0]?.id : "todo")) === c.key); return (
        <div key={c.key} onDragOver={(e) => { e.preventDefault(); setOver(c.key); }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, c.key)} data-testid={`kanban-col-${c.key}`}
          className={cn("w-72 flex-shrink-0 rounded-2xl p-2.5 transition-colors", over === c.key ? "bg-sky-50 ring-2 ring-sky-200" : "bg-slate-100/70")}>
          <div className="flex items-center justify-between px-1.5 mb-2"><span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{c.label}</span><span className="text-[11px] text-slate-400 tabular">{list.length}</span></div>
          <div className="space-y-2 min-h-[60px]">{list.map((i) => <Card key={i.id} item={i} onChanged={onChanged} />)}</div>
        </div>); })}
    </div>);
}
